import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ReviewOverviewDashboard from './components/ReviewOverviewDashboard';
import ReviewSubmissionForm from './components/ReviewSubmissionForm';
import TestimonialModerationPanel from './components/TestimonialModerationPanel';
import SuccessMetricsTracker from './components/SuccessMetricsTracker';
import clientReviewService from '../../services/clientReviewService';
import aiCaseStudyService from '../../services/aiCaseStudyService';

const ClientReviewTestimonialManagementCenter = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [transforming, setTransforming] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState(null);
  const [caseStudyModal, setCaseStudyModal] = useState(false);
  const [generatedCaseStudy, setGeneratedCaseStudy] = useState(null);

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      setLoading(true);
      // Load reviews for moderation (submitted status)
      const moderationData = await clientReviewService?.getReviewsForModeration();
      // Load published reviews
      const publishedData = await clientReviewService?.getPublishedReviews();
      // Combine both
      setReviews([...moderationData, ...publishedData]);
    } catch (error) {
      console.error('Failed to load reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCaseStudy = async (reviewId) => {
    try {
      setTransforming(true);
      setSelectedReviewId(reviewId);
      const result = await clientReviewService?.generateCaseStudy(reviewId);
      setGeneratedCaseStudy(result?.caseStudy);
      setCaseStudyModal(true);
      await loadReviews();
    } catch (error) {
      alert(error?.message || 'Failed to generate case study');
    } finally {
      setTransforming(false);
      setSelectedReviewId(null);
    }
  };

  const handleBatchTransform = async () => {
    try {
      setTransforming(true);
      const reviewIds = reviews?.filter(r => !r?.caseStudyContent)?.map(r => r?.id);
      if (reviewIds?.length === 0) {
        alert('No reviews available for transformation');
        return;
      }
      const result = await aiCaseStudyService?.batchTransformReviews(reviewIds);
      alert(`Transformed ${result?.successful} of ${result?.totalProcessed} reviews`);
      await loadReviews();
    } catch (error) {
      alert(error?.message || 'Failed to batch transform reviews');
    } finally {
      setTransforming(false);
    }
  };

  const handleStatusUpdate = async (reviewId, status, notes) => {
    try {
      if (status === 'approved') {
        await clientReviewService?.approveReviewWithAutoPublish(reviewId, notes, 'Manager');
      } else if (status === 'rejected') {
        await clientReviewService?.rejectReview(reviewId, notes, 'Manager');
      }
      await loadReviews();
    } catch (error) {
      throw error;
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'BarChart3' },
    { id: 'submit', label: 'Submit Review', icon: 'PenTool' },
    { id: 'moderation', label: 'Moderation', icon: 'Shield' },
    { id: 'metrics', label: 'Success Metrics', icon: 'TrendingUp' },
  ];

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Client Review & Testimonial Center</h1>
              <p className="text-muted-foreground mt-1">
                Manage client reviews and generate AI-powered case studies
              </p>
            </div>
            <Button
              onClick={handleBatchTransform}
              disabled={transforming}
              variant="default"
              size="sm"
            >
              <Icon name="Sparkles" size={16} className="mr-2" />
              {transforming ? 'Transforming...' : 'Batch Generate Case Studies'}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-border">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 ${
                activeTab === tab?.id
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={18} />
              {tab?.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div>
          {activeTab === 'overview' && (
            <ReviewOverviewDashboard
              reviews={reviews}
              onGenerateCaseStudy={handleGenerateCaseStudy}
              transforming={transforming}
              selectedReviewId={selectedReviewId}
            />
          )}
          {activeTab === 'submit' && <ReviewSubmissionForm onReviewSubmitted={loadReviews} />}
          {activeTab === 'moderation' && (
            <TestimonialModerationPanel 
              reviews={reviews}
              onStatusUpdate={handleStatusUpdate}
              onRefresh={loadReviews}
            />
          )}
          {activeTab === 'metrics' && <SuccessMetricsTracker reviews={reviews} />}
        </div>
      </div>

      {/* Case Study Modal */}
      {caseStudyModal && generatedCaseStudy && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-xl font-bold text-foreground">Generated Case Study</h2>
              <button
                onClick={() => setCaseStudyModal(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Executive Summary */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Executive Summary</h3>
                <p className="text-muted-foreground">{generatedCaseStudy?.executive_summary}</p>
              </div>

              {/* Challenge */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Challenge</h3>
                <p className="text-muted-foreground">{generatedCaseStudy?.challenge}</p>
              </div>

              {/* Solution */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Solution</h3>
                <p className="text-muted-foreground">{generatedCaseStudy?.solution}</p>
              </div>

              {/* Implementation Timeline */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Implementation Timeline</h3>
                <p className="text-muted-foreground">{generatedCaseStudy?.implementation_timeline}</p>
              </div>

              {/* Quantifiable Results */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Quantifiable Results</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {generatedCaseStudy?.quantifiable_results?.map((result, index) => (
                    <div key={index} className="bg-muted rounded-lg p-4">
                      <div className="text-sm text-muted-foreground mb-1">{result?.metric}</div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Before: {result?.before}</span>
                        <Icon name="ArrowRight" size={14} className="text-muted-foreground" />
                        <span className="text-sm font-semibold text-primary">After: {result?.after}</span>
                      </div>
                      <div className="text-xs text-success mt-1">{result?.improvement}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ROI Calculation */}
              <div className="bg-primary/10 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-foreground mb-2">ROI Calculation</h3>
                <div className="grid grid-cols-3 gap-4 mb-3">
                  <div>
                    <div className="text-sm text-muted-foreground">Total Savings</div>
                    <div className="text-2xl font-bold text-primary">
                      K{generatedCaseStudy?.roi_calculation?.total_savings_kina?.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">ROI</div>
                    <div className="text-2xl font-bold text-success">
                      {generatedCaseStudy?.roi_calculation?.roi_percentage}%
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Payback Period</div>
                    <div className="text-2xl font-bold text-foreground">
                      {generatedCaseStudy?.roi_calculation?.payback_period_months} months
                    </div>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  {generatedCaseStudy?.roi_calculation?.calculation_details}
                </p>
              </div>

              {/* Key Takeaways */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Key Takeaways</h3>
                <ul className="space-y-2">
                  {generatedCaseStudy?.key_takeaways?.map((takeaway, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Icon name="CheckCircle" size={16} className="text-success mt-1" />
                      <span className="text-muted-foreground">{takeaway}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Testimonial */}
              <div className="bg-muted rounded-lg p-4 border-l-4 border-primary">
                <p className="text-foreground italic mb-2">"{generatedCaseStudy?.testimonial_quote}"</p>
                <div className="text-sm text-muted-foreground">
                  <strong>{generatedCaseStudy?.testimonial_author}</strong>
                  {generatedCaseStudy?.testimonial_position && (
                    <span>, {generatedCaseStudy?.testimonial_position}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-border flex justify-end">
              <Button onClick={() => setCaseStudyModal(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientReviewTestimonialManagementCenter;